import { useState, useEffect, useCallback } from "react";
import { Accessibility, X, ZoomIn, ZoomOut, RotateCcw, Type, Contrast, Link2, MousePointer2 } from "lucide-react";

const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [textSize, setTextSize] = useState(() => {
    return parseInt(localStorage.getItem("a11y-text-size") || "100", 10);
  });
  const [contrastMode, setContrastMode] = useState(() => {
    return localStorage.getItem("a11y-contrast") || "normal";
  });
  const [highlightLinks, setHighlightLinks] = useState(() => {
    return localStorage.getItem("a11y-highlight-links") === "true";
  });
  const [bigCursor, setBigCursor] = useState(() => {
    return localStorage.getItem("a11y-big-cursor") === "true";
  });

  // Apply text size
  useEffect(() => {
    document.documentElement.style.fontSize = `${textSize}%`;
    localStorage.setItem("a11y-text-size", textSize.toString());
  }, [textSize]);

  // Apply contrast mode
  useEffect(() => {
    document.documentElement.classList.remove("a11y-high-contrast", "a11y-dark-contrast", "a11y-invert");
    if (contrastMode === "high") {
      document.documentElement.classList.add("a11y-high-contrast");
    } else if (contrastMode === "dark") {
      document.documentElement.classList.add("a11y-dark-contrast");
    } else if (contrastMode === "invert") {
      document.documentElement.classList.add("a11y-invert");
    }
    localStorage.setItem("a11y-contrast", contrastMode);
  }, [contrastMode]);

  // Apply highlight links
  useEffect(() => {
    if (highlightLinks) {
      document.documentElement.classList.add("a11y-highlight-links");
    } else {
      document.documentElement.classList.remove("a11y-highlight-links");
    }
    localStorage.setItem("a11y-highlight-links", highlightLinks.toString());
  }, [highlightLinks]);

  // Apply big cursor
  useEffect(() => {
    if (bigCursor) {
      document.documentElement.classList.add("a11y-big-cursor");
    } else {
      document.documentElement.classList.remove("a11y-big-cursor");
    }
    localStorage.setItem("a11y-big-cursor", bigCursor.toString());
  }, [bigCursor]);

  const cycleContrast = useCallback(() => {
    const modes = ["normal", "high", "dark", "invert"];
    const idx = modes.indexOf(contrastMode);
    setContrastMode(modes[(idx + 1) % modes.length]);
  }, [contrastMode]);

  const contrastLabel = {
    normal: "Normal",
    high: "High Contrast",
    dark: "Dark",
    invert: "Inverted",
  };

  const resetAll = useCallback(() => {
    setTextSize(100);
    setContrastMode("normal");
    setHighlightLinks(false);
    setBigCursor(false);
  }, []);

  const increaseText = () => setTextSize((s) => Math.min(s + 10, 150));
  const decreaseText = () => setTextSize((s) => Math.max(s - 10, 70));

  return (
    <div className="fixed bottom-6 left-6 z-[9999]" data-testid="accessibility-widget">
      {/* Panel */}
      {open && (
        <div
          className="absolute bottom-16 left-0 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
          data-testid="accessibility-panel"
          role="dialog"
          aria-label="Accessibility Options"
        >
          {/* Header */}
          <div className="bg-[#0EA5E9] px-4 py-3 flex items-center gap-2">
            <Accessibility className="w-5 h-5 text-white" />
            <span className="text-white font-semibold text-sm">Accessibility Options</span>
          </div>

          <div className="p-3 space-y-1">
            {/* Text Size */}
            <div className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-2.5">
                <Type className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">Text Size ({textSize}%)</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={decreaseText}
                  className="w-7 h-7 flex items-center justify-center rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                  aria-label="Decrease text size"
                  data-testid="a11y-text-decrease"
                >
                  <ZoomOut className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={increaseText}
                  className="w-7 h-7 flex items-center justify-center rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                  aria-label="Increase text size"
                  data-testid="a11y-text-increase"
                >
                  <ZoomIn className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Contrast */}
            <button
              onClick={cycleContrast}
              className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="a11y-contrast"
            >
              <div className="flex items-center gap-2.5">
                <Contrast className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">Contrast: {contrastLabel[contrastMode]}</span>
              </div>
            </button>

            {/* Highlight Links */}
            <button
              onClick={() => setHighlightLinks(!highlightLinks)}
              className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="a11y-highlight-links"
            >
              <div className="flex items-center gap-2.5">
                <Link2 className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">Highlight Links</span>
              </div>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${highlightLinks ? "bg-[#0EA5E9] text-white" : "bg-gray-200 text-gray-500"}`}>
                {highlightLinks ? "ON" : "OFF"}
              </span>
            </button>

            {/* Big Cursor */}
            <button
              onClick={() => setBigCursor(!bigCursor)}
              className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="a11y-big-cursor"
            >
              <div className="flex items-center gap-2.5">
                <MousePointer2 className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">Big Cursor</span>
              </div>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${bigCursor ? "bg-[#0EA5E9] text-white" : "bg-gray-200 text-gray-500"}`}>
                {bigCursor ? "ON" : "OFF"}
              </span>
            </button>
          </div>

          {/* Reset */}
          <div className="px-3 pb-2">
            <button
              onClick={resetAll}
              className="flex items-center justify-center gap-2 w-full py-2 bg-[#0EA5E9] hover:bg-[#0284C7] text-white text-sm font-semibold rounded-lg transition-colors"
              data-testid="a11y-reset"
            >
              <RotateCcw className="w-4 h-4" />
              Reset All
            </button>
          </div>

          {/* Statement Link */}
          <div className="px-3 pb-3 pt-1">
            <a
              href="/accessibility-statement"
              className="text-[#0EA5E9] hover:text-[#0284C7] text-xs font-medium hover:underline"
              data-testid="a11y-statement-link"
            >
              View Accessibility Statement
            </a>
          </div>
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={() => setOpen(!open)}
        className="w-12 h-12 rounded-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105"
        aria-label={open ? "Close Accessibility Options" : "Open Accessibility Options"}
        data-testid="accessibility-btn"
      >
        {open ? <X className="w-5 h-5" /> : <Accessibility className="w-6 h-6" />}
      </button>
    </div>
  );
};

export default AccessibilityWidget;
